import { useState, useEffect, useRef } from 'react';
import { solutions } from '@/mocks/homeContent';
import SolutionModal from './SolutionModal';
import type { SolutionModalData } from './SolutionModal';

export default function SolutionsSection() {
  const [selected, setSelected] = useState<SolutionModalData | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const reveals = el.querySelectorAll('.reveal');
            reveals.forEach((r) => r.classList.add('visible'));
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="solutions" className="relative py-24 md:py-32 px-4 md:px-6 lg:px-12 bg-background-50">
      <div className="max-w-[1440px] mx-auto">
        {/* Header - two column */}
        <div className="reveal flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-14">
          <div>
            <span className="inline-flex items-center px-4 py-1.5 rounded-full border border-background-300/60 text-xs font-medium text-foreground-600 tracking-wide uppercase bg-transparent mb-4">
              Featured Solutions
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 tracking-tight max-w-xl">
              Everything your business needs to run and grow
            </h2>
          </div>
          <p className="text-sm md:text-base text-foreground-600 max-w-md leading-relaxed lg:text-right">
            From foundational IT infrastructure to custom SaaS platforms, we cover the full technology spectrum.
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {solutions.map((solution, idx) => (
            <div
              key={solution.id}
              onClick={() => setSelected(solution)}
              className={`reveal reveal-delay-${idx + 1} group relative overflow-hidden rounded-2xl bg-background-100 border border-background-200/60 hover:border-background-300/80 hover:shadow-lg transition-all duration-300 cursor-pointer`}
            >
              {/* Banner image area - CSS gradient instead of photo */}
              <div className="relative h-48 overflow-hidden">
                <div className="absolute inset-0 bg-[linear-gradient(135deg,oklch(var(--primary-700)/0.9),oklch(var(--background-800)/0.95))]">
                  <div
                    className="absolute inset-0 opacity-[0.06]"
                    style={{
                      backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 20px, oklch(var(--background-50)/0.5) 20px, oklch(var(--background-50)/0.5) 21px)`,
                    }}
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-background-100 via-background-100/40 to-transparent" />
                <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-background-50/90 backdrop-blur-sm text-foreground-700 border border-background-200/40">
                  {solution.category}
                </span>
              </div>

              {/* Content */}
              <div className="p-5">
                <div className="flex items-start gap-3 mb-2">
                  <div className="w-9 h-9 rounded-lg bg-primary-100 dark:bg-primary-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className={`${solution.icon} text-sm text-primary-600 dark:text-primary-400 w-4 h-4 flex items-center justify-center`} />
                  </div>
                  <h3 className="text-sm md:text-base font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors duration-300 leading-snug">
                    {solution.title}
                  </h3>
                </div>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                  {solution.description}
                </p>
                <div className="mt-4 flex items-center gap-1 text-xs font-semibold text-primary-500 group-hover:opacity-100 transition-all duration-300">
                  <span>Learn more</span>
                  <i className="ri-arrow-right-line text-xs w-3 h-3 flex items-center justify-center group-hover:translate-x-0.5 transition-transform duration-300" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <SolutionModal solution={selected} onClose={() => setSelected(null)} />
    </section>
  );
}